import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Markdown } from "../components/chat/Markdown";
import {
  IconChevron, IconFile, IconSpark, IconUpload, IconGitBranch, IconGrid,
} from "../components/icons";
import { schedulerApi, friendlyError } from "../lib/schedulerApi";
import type {
  SchedulerSkillInfo,
  SchedulerGithubRepoInfo,
  SchedulerSkillDetail,
  SchedulerSkillFile,
} from "@shared/types";

/**
 * 团队技能 library (P2d-4c: extracted from SchedulerPage into the shared
 * skills home so the agent-builder surfaces open it IN PLACE — the
 * 建助手 → 日程页 → 弹窗 two-hop upload path was walked and rejected on
 * 2026-07-06). Same component, same API (/api/scheduler/skills*), two
 * callers: the scheduler page and the assistants detail/create surfaces.
 * Styles ride scheduler.css (sch- and sk- classes), globally loaded.
 */

/** Admin panel: upload (.tar.gz) / list / uninstall external skills. */
export function SkillsManager(props: { onUse: (s: SchedulerSkillInfo) => void; onClose: () => void }): JSX.Element {
  const [skills, setSkills] = useState<SchedulerSkillInfo[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tenant, setTenant] = useState("");
  const [detailFor, setDetailFor] = useState<SchedulerSkillInfo | null>(null);
  // Add flow (kit skills-modal-01/03/04): a dropdown picks 上传 / GitHub 导入.
  const [mode, setMode] = useState<"list" | "upload" | "github">("list");
  const [addOpen, setAddOpen] = useState(false);
  // The add menu is rendered fixed-positioned off the button rect so it escapes
  // the modal's `overflow:auto` clip (it was cut off when the list was short).
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const [addPos, setAddPos] = useState<{ top: number; right: number } | null>(null);
  const toggleAdd = useCallback(() => {
    setAddOpen((open) => {
      if (!open && addBtnRef.current) {
        const r = addBtnRef.current.getBoundingClientRect();
        setAddPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
      }
      return !open;
    });
  }, []);
  const [ghUrl, setGhUrl] = useState("");
  const [ghRepo, setGhRepo] = useState<SchedulerGithubRepoInfo | null>(null);
  const [ghChecking, setGhChecking] = useState(false);

  const load = useCallback(async () => {
    try {
      setSkills((await schedulerApi.skills()).skills);
    } catch (e) {
      setErr(friendlyError(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const backToList = (): void => { setMode("list"); setErr(null); setGhUrl(""); setGhRepo(null); };
  const onUpload = (file: File): void => {
    setBusy(true);
    setErr(null);
    void schedulerApi
      .uploadSkill(file, tenant.trim())
      .then(() => load())
      .then(() => backToList())
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };
  const validateGh = (): void => {
    if (ghUrl.trim().length === 0) return;
    setGhChecking(true);
    setErr(null);
    setGhRepo(null);
    void schedulerApi
      .validateGithubSkill(ghUrl.trim())
      .then((r) => setGhRepo(r.repo))
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setGhChecking(false));
  };
  const importGh = (): void => {
    setBusy(true);
    setErr(null);
    void schedulerApi
      .importGithubSkill(ghUrl.trim(), tenant.trim())
      .then(() => load())
      .then(() => backToList())
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };
  const onUninstall = (s: SchedulerSkillInfo): void => {
    void schedulerApi.uninstallSkill(s.tenant, s.name).then(() => load()).catch((e: unknown) => setErr(friendlyError(e)));
  };

  // The 归属团队 field, shown atop the upload / github forms.
  const tenantField = (
    <div className="sch-field">
      <label>归属团队（留空=你的团队）</label>
      <input className="sch-in" value={tenant} placeholder="如 acme" onChange={(e) => setTenant(e.target.value)} />
    </div>
  );

  // Upload view (kit skills-modal-04): a drop-zone + format requirements.
  if (mode === "upload") {
    return (
      <div className="sch-modal-bg" onClick={props.onClose}>
        <div className="sch-modal narrow" onClick={(e) => e.stopPropagation()}>
          <div className="sch-modal-head">
            <div className="sk-head">
              <button className="btn ghost sm" onClick={backToList}>← 返回</button>
              <h3 style={{ margin: 0 }}>上传技能包</h3>
            </div>
            <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
          </div>
          {err !== null && <div className="sch-error" role="alert">{err}</div>}
          <label
            className="sk-drop"
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f !== undefined && !busy) onUpload(f);
            }}
          >
            <span className="sk-drop-stack"><i /><i /><i /></span>
            <span className="sk-drop-text">{busy ? "上传中…" : "拖入，或点击上传"}</span>
            <span className="sk-drop-hint">.zip / .tar.gz，最大 50MB</span>
            <input
              type="file"
              accept=".zip,.tar.gz,.tgz,application/zip,application/gzip"
              style={{ display: "none" }}
              disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f !== undefined) onUpload(f); e.target.value = ""; }}
            />
          </label>
          {tenantField}
          <div className="sk-req">
            <div className="sch-overline" style={{ marginTop: 0 }}>格式要求</div>
            <ul>
              <li>压缩包里有一个技能文件夹，根目录含 <code>SKILL.md</code>。</li>
              <li><code>SKILL.md</code> 顶部是 YAML（<code>name:</code>、<code>description:</code>，可选 <code>tools:</code>）。</li>
              <li>可执行脚本放 <code>scripts/</code>；小环只在沙箱里运行它，不碰目录外的代码。</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // GitHub import view (kit skills-modal-03): URL → validate → found card → import.
  if (mode === "github") {
    return (
      <div className="sch-modal-bg" onClick={props.onClose}>
        <div className="sch-modal narrow" onClick={(e) => e.stopPropagation()}>
          <div className="sch-modal-head">
            <div className="sk-head">
              <button className="btn ghost sm" onClick={backToList}>← 返回</button>
              <h3 style={{ margin: 0 }}>从 GitHub 导入</h3>
            </div>
            <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
          </div>
          <p className="sch-subtitle" style={{ marginTop: 0 }}>粘贴公开仓库地址，小环会自动拉取并校验 SKILL.md。</p>
          {err !== null && <div className="sch-error" role="alert">{err}</div>}
          {tenantField}
          <div className="sch-field">
            <label>仓库 URL</label>
            <div className="sch-webhook">
              <input
                className="sch-in"
                value={ghUrl}
                placeholder="https://github.com/org/repo（或指到子目录 / SKILL.md）"
                onChange={(e) => { setGhUrl(e.target.value); setGhRepo(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") validateGh(); }}
              />
              <button className="btn ghost" onClick={validateGh} disabled={ghChecking || ghUrl.trim().length === 0}>
                {ghChecking ? "检查中…" : "检查"}
              </button>
            </div>
            <p className="sch-note">支持整仓、子目录，或直接指向 SKILL.md 文件。</p>
          </div>
          {ghRepo !== null && (
            <div className="sk-gh-found">
              <span className="sk-gh-tick">✓</span>
              <div>
                <div className="sk-gh-title">找到仓库 · {ghRepo.fullName}</div>
                <div className="sk-gh-sub">{ghRepo.visibility} · {ghRepo.license} · {Math.round(ghRepo.sizeKb / 1024 * 10) / 10} MB · 最近提交 {ghRepo.lastCommit.slice(0, 10)}</div>
              </div>
            </div>
          )}
          <div className="sch-form-actions">
            <button className="btn ghost" onClick={backToList}>取消</button>
            <button className="btn" onClick={importGh} disabled={busy || ghRepo === null}>
              {busy ? "导入中…" : "导入技能"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sch-modal-bg" onClick={() => { setAddOpen(false); props.onClose(); }}>
      <div className="sch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sch-modal-head">
          <div>
            <h3>团队技能</h3>
            <p className="sch-subtitle">添加你们团队自己的技能包，建任务时就能让小环用上。</p>
          </div>
          <div className="sch-li-actions">
            <div className="sk-add">
              <button ref={addBtnRef} className="btn sm" onClick={toggleAdd}>添加技能 ▾</button>
              {addOpen && (
                <>
                  <div className="rcard-menu-backdrop" onClick={() => setAddOpen(false)} />
                  <div
                    className="sk-add-menu"
                    style={addPos !== null ? { position: "fixed", top: addPos.top, right: addPos.right, left: "auto" } : undefined}
                  >
                    <button className="sk-add-item" onClick={() => { setAddOpen(false); setErr(null); setMode("upload"); }}>
                      <span className="sk-add-glyph"><IconUpload /></span>
                      <span><b>上传本地包</b><span className="sk-add-desc">拖入技能文件夹或 .zip / .tar.gz（含 SKILL.md）</span></span>
                    </button>
                    <button className="sk-add-item" onClick={() => { setAddOpen(false); setErr(null); setMode("github"); }}>
                      <span className="sk-add-glyph"><IconGitBranch /></span>
                      <span><b>从 GitHub 导入</b><span className="sk-add-desc">粘贴公开仓库 URL，自动拉取并校验</span></span>
                    </button>
                    <button className="sk-add-item" disabled>
                      <span className="sk-add-glyph"><IconGrid /></span>
                      <span><b>官方库 <span className="sch-skill-tag">即将支持</span></b><span className="sk-add-desc">浏览 Cycle 精选技能市场</span></span>
                    </button>
                    <button className="sk-add-item" disabled>
                      <span className="sk-add-glyph"><IconSpark /></span>
                      <span><b>用 Cycle 生成 <span className="sch-skill-tag">即将支持</span></b><span className="sk-add-desc">描述要做什么，小环起草 SKILL.md 与初始代码</span></span>
                    </button>
                  </div>
                </>
              )}
            </div>
            <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
          </div>
        </div>

        {err !== null && <div className="sch-error" role="alert">{err}</div>}

        <div className="sch-overline">已安装</div>
        {skills === null && <p className="sch-muted">加载中…</p>}
        {skills !== null && skills.length === 0 && <p className="sch-muted">还没有技能，点右上「添加技能」开始。</p>}
        {skills !== null && skills.length > 0 && (
          <div className="sch-skill-grid">
            {skills.map((s) => (
              <div
                key={`${s.tenant}/${s.name}`}
                className="sch-skill-card"
                role="button"
                tabIndex={0}
                onClick={() => setDetailFor(s)}
                onKeyDown={(e) => { if (e.key === "Enter") setDetailFor(s); }}
              >
                <div className="sch-skill-card-top">
                  <span className="sch-skill-glyph"><IconFile /></span>
                  <div className="sch-skill-card-main">
                    <div className="sch-skill-card-name">
                      <span className="sch-skill-card-title">{s.name}</span>
                      {s.hasScripts && <span className="sch-skill-tag">含脚本</span>}
                      <IconChevron className="sch-li-chev" />
                    </div>
                    <p className="sch-skill-card-desc">{s.description}</p>
                  </div>
                </div>
                <div className="sch-skill-card-foot" onClick={(e) => e.stopPropagation()}>
                  <span className="sch-skill-card-author">{s.tenant}</span>
                  <span className="sch-li-actions">
                    <button className="btn ghost sm" onClick={() => props.onUse(s)}>用此技能建任务</button>
                    <button className="btn ghost sm danger" onClick={() => onUninstall(s)}>卸载</button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detailFor !== null && (
        <SkillDetail
          tenant={detailFor.tenant}
          name={detailFor.name}
          onUse={(s) => { setDetailFor(null); props.onUse(s); }}
          onUninstall={() => { const s = detailFor; setDetailFor(null); onUninstall(s); }}
          onClose={() => setDetailFor(null)}
        />
      )}
    </div>
  );
}

/**
 * Skill detail (adapted from the DS skills-modal-06-detail kit): a file-tree
 * sidebar + a preview pane. SKILL.md renders as a doc; other files show as code.
 * Read-only — uploaded skills are inspected here, not edited.
 */
function SkillDetail(props: {
  tenant: string;
  name: string;
  onUse: (s: SchedulerSkillInfo) => void;
  onUninstall: () => void;
  onClose: () => void;
}): JSX.Element {
  const [detail, setDetail] = useState<SchedulerSkillDetail | null>(null);
  const [active, setActive] = useState<string>("SKILL.md");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    schedulerApi
      .skillDetail(props.tenant, props.name)
      .then((r) => {
        if (!live) return;
        setDetail(r.skill);
        setActive(r.skill.files[0]?.path ?? "SKILL.md");
      })
      .catch((e: unknown) => live && setErr(friendlyError(e)));
    return () => { live = false; };
  }, [props.tenant, props.name]);

  const file = detail?.files.find((f) => f.path === active);
  const isMarkdown = active.toLowerCase().endsWith(".md");
  const treeRows = useMemo(() => buildSkillTree(detail?.files ?? []), [detail]);
  const indent = (depth: number): { paddingLeft: string } => ({
    paddingLeft: `calc(var(--space-4) + ${depth} * var(--space-4))`,
  });
  return (
    <div className="sch-modal-bg sk-detail-bg" onClick={props.onClose}>
      <div className="sch-modal sk-detail" onClick={(e) => e.stopPropagation()}>
        <div className="sch-modal-head">
          <div className="sk-head">
            <h3>{detail?.name ?? props.name}</h3>
            {detail?.hasScripts === true && <span className="sch-skill-tag">含脚本</span>}
            <span className="sch-muted sk-head-tenant">{props.tenant}</span>
          </div>
          <div className="sch-li-actions">
            {detail !== null && <button className="btn ghost sm" onClick={() => props.onUse(detail)}>用此技能建任务</button>}
            <button className="btn ghost sm danger" onClick={props.onUninstall}>卸载</button>
            <button className="btn ghost sm" onClick={props.onClose}>关闭</button>
          </div>
        </div>
        {detail?.description !== undefined && detail.description.length > 0 && (
          <p className="sch-subtitle sk-desc">{detail.description}</p>
        )}
        {err !== null && <div className="sch-error" role="alert">{err}</div>}
        <div className="sk-body">
          <div className="sk-tree">
            {treeRows.map((row) =>
              row.kind === "folder" ? (
                <div key={`d:${row.depth}:${row.path}`} className="sk-tree-folder" style={indent(row.depth)}>
                  <IconChevron className="sk-tree-ic" />
                  {row.name}
                </div>
              ) : (
                <button
                  key={row.path}
                  className={`sk-tree-item ${row.path === active ? "on" : ""}`}
                  style={indent(row.depth)}
                  onClick={() => setActive(row.path)}
                >
                  <IconFile className="sk-tree-ic" />
                  <span className="sk-tree-name">{row.name}</span>
                </button>
              ),
            )}
          </div>
          <div className="sk-preview">
            {detail === null && err === null && <p className="sch-muted">加载中…</p>}
            {file !== undefined && isMarkdown && (
              <article className="sk-doc"><Markdown>{stripFrontmatter(file.content)}</Markdown></article>
            )}
            {file !== undefined && !isMarkdown && <pre className="sk-code">{file.content}</pre>}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Strip a leading YAML frontmatter block (`---\n…\n---`) from a markdown doc so
 * a skill's SKILL.md renders its body cleanly — otherwise the frontmatter shows
 * as a run-on blob (the `---` becomes an <hr>, `description: >` a blockquote).
 * The name/description already appear in the dialog head, so this isn't a loss.
 */
function stripFrontmatter(md: string): string {
  const body = md.charCodeAt(0) === 0xfeff ? md.slice(1) : md;
  const m = /^---\r?\n[\s\S]*?\r?\n---[ \t]*\r?\n?/.exec(body);
  return m !== null ? body.slice(m[0].length).replace(/^\s+/, "") : body;
}

/** A row in the rendered file tree: a folder header or a (clickable) file. */
interface SkillTreeRow {
  kind: "folder" | "file";
  name: string;
  depth: number;
  /** Full path (files) or folder name (folders). */
  path: string;
}

/**
 * Turn a flat file list into a nested file tree (folders before files, SKILL.md
 * first), flattened to indented rows for rendering — mirrors the DS kit tree.
 */
function buildSkillTree(files: SchedulerSkillFile[]): SkillTreeRow[] {
  interface Node { children: Map<string, Node>; file?: string }
  const root: Node = { children: new Map() };
  for (const f of files) {
    let node = root;
    const parts = f.path.split("/");
    parts.forEach((part, i) => {
      let child = node.children.get(part);
      if (child === undefined) {
        child = { children: new Map() };
        node.children.set(part, child);
      }
      if (i === parts.length - 1) child.file = f.path;
      node = child;
    });
  }
  const rows: SkillTreeRow[] = [];
  const walk = (node: Node, depth: number): void => {
    const entries = [...node.children.entries()].sort((a, b) => {
      const aFile = a[1].file !== undefined;
      const bFile = b[1].file !== undefined;
      if (aFile !== bFile) return aFile ? 1 : -1; // folders first
      if (a[0] === "SKILL.md") return -1;
      if (b[0] === "SKILL.md") return 1;
      return a[0].localeCompare(b[0]);
    });
    for (const [name, child] of entries) {
      if (child.file !== undefined) rows.push({ kind: "file", name, depth, path: child.file });
      else {
        rows.push({ kind: "folder", name, depth, path: name });
        walk(child, depth + 1);
      }
    }
  };
  walk(root, 0);
  return rows;
}
